import WandbLoader from '@wandb/weave/common/components/WandbLoader';
// @todo: Migrate to @wandb/components. For more details see:
// https://www.notion.so/wandbai/Migrating-components-207e2f5c7ef380129201f72a5770a8db
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components';
import * as DropdownMenu from '@wandb/weave/components/DropdownMenu';
import {Icon} from '@wandb/weave/components/Icon';
import * as Panel2 from '@wandb/weave/components/Panel2/panel';
import {
  constNone,
  constString,
  opArtifactId,
  opArtifactMembershipArtifactVersion,
  opArtifactMembershipForAlias,
  opArtifactMembershipVersionIndex,
  opArtifactVersionId,
  opDict,
  OutputNode,
} from '@wandb/weave/core';
import {useNodeValue, useRefreshAllNodes} from '@wandb/weave/react';
import * as React from 'react';
import {useContext, useRef, useState} from 'react';
import {useHistory} from 'react-router-dom';
import {toast} from 'react-toastify';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Dropdown} from 'semantic-ui-react';

import {envIsPublicCloud} from '../../../../config';
import {
  useMoveArtifactCollectionMutation,
  useUpdateArtifactCollectionMutation,
  useUpdateArtifactPortfolioMutation,
} from '../../../../generated/graphql';
import {useHandleScroll} from '../../../../pages/HomePage/HomePageSidebar/useHandleScroll';
import {DEFAULT_ONPREM_ORGANIZATION_NAME} from '../../../../pages/OrgDashboard/OrgRoles/RolesDashboard';
import {Analytics} from '../../../../services/analytics';
import {
  PremiumFeatureTooltip,
  useIsAutomationEnabled,
} from '../../../../util/accounts/pricing';
import {isVersionAlias} from '../../../../util/artifacts';
import {propagateErrorsContext} from '../../../../util/errors';
import {trackCreateTriggerClicked} from '../../../../util/navigation';
import * as urls from '../../../../util/urls';
import ArtifactActionHistory from '../../../ArtifactActionHistory';
import {CreateTriggerDrawerForCollection} from '../../../ArtifactCollectionSettings/CreateTriggerModal';
import {ArtifactVersionsTable} from '../../../ArtifactOverview/ArtifactVersionsTable';
import {useArtifactCollectionOverview} from '../../../ArtifactOverview/hooks';
import {
  DeleteArtifactModal,
  MoveArtifactModal,
  RenameArtifactModal,
} from '../../../ArtifactSidebar/ArtifactSidebarActions';
import {useIsProjectReadOnly} from '../../../Registries/common/hooks';
import {
  checkRegistryProject,
  getCollectionRegistryName,
  REGISTRY_PROJECT_PREFIX,
  resolveRegistryUrl,
  showForRegistry,
} from '../../../Registries/common/utils';
import {MigrationStatusBanner} from '../../../Registries/ModelRegistryMigration/MigrationStatusBanner';
import {ModelRegistryMigrationContext} from '../../../Registries/ModelRegistryMigration/ModelRegistryMigrationContextProvider';
import {MigrationStatus} from '../../../Registries/ModelRegistryMigration/utils';
import {AddArtifactDrawer} from '../../../Registries/workflows/CreateRegisteredCollectionDrawer/AddArtifactDrawer/AddArtifactDrawer';
import {AccountSelectorContext} from '../../../Search/SearchNav/AccountSelectorContextProvider';
import {
  ConfigType as PanelArtifactMembershipConfigType,
  PanelArtifactMembership,
} from '../../artifactMembership/PanelArtifactMembership';
import {PanelArtifactMembershipOverviewCollectionSection} from '../../artifactMembership/PanelArtifactMembershipOverview/PanelArtifactMembershipOverviewCollectionSection';
import {TriggersOverviewTable} from '../../artifactMembership/PanelArtifactMembershipOverview/TriggersOverviewTable';
import {PanelEmptyArtifact} from '../PanelEmptyArtifact';
// eslint-disable-next-line import/no-cycle -- please fix if you can
import {
  ArtifactCollectionViewType,
  isArtifactCollectionView,
} from './ArtifactPanelRoot';
import * as S from './style';

const inputType = 'artifact' as const;

type selectedCollectionViewType = 'membership' | ArtifactCollectionViewType;

export type ConfigType = {
  // selectedCollectionView determines the collection-level view selected - null should default to membership
  selectedCollectionView?: selectedCollectionViewType;
  tabConfigs?: {
    overview?: PanelArtifactMembershipConfigType; // for legacy reasons, `overview` is used for `membership` config
  };
  // The following field is only applicable when selectedCollectionView is 'membership'
  // selectedMembershipIdentifier determines the membership identifier selected - null should default to latest membership version
  selectedMembershipIdentifier?: string;
};

const PanelArtifact: React.FC<
  Panel2.PanelProps<typeof inputType, ConfigType> & {artifactRegistry?: boolean}
> = props => {
  // Model Registry Migration Context
  const {
    entityMigrationStatus: MRMigrationStatus,
    isMRReadOnly: readOnlyBasedOnMigrationStatus,
  } = useContext(ModelRegistryMigrationContext);

  // 1. Extract Props
  const {updateConfig, config} = props;
  const artifactCollectionNode = props.input;
  const collectionView = config?.selectedCollectionView;
  const membershipIdentifier = config?.selectedMembershipIdentifier;

  // 2. Component State
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [collectionRenameModalOpen, setCollectionRenameModalOpen] =
    React.useState(false);
  const [collectionDeleteModalOpen, setCollectionDeleteModalOpen] =
    React.useState(false);
  const [isMovingCollection, setMovingCollection] = useState(false);
  const [addTriggerModalOpen, setAddTriggerModalOpen] = React.useState(false);
  const [isLinkArtifactModalOpen, setIsLinkArtifactModalOpen] = useState(false);

  // 3. Initialize Child Configs
  const tabConfig = Panel2.useConfigChild('tabConfigs', config, updateConfig);
  const membershipConfig = Panel2.useConfigChild(
    'overview',
    tabConfig.config,
    tabConfig.updateConfig
  );

  // 4. General Hooks
  const refreshAll = useRefreshAllNodes();
  const ref = useRef<HTMLDivElement | null>(null);
  // TODO: This should be removed - we don't want URL management done
  // in panels - very bad
  const history = useHistory();
  const [updateArtifactPortfolio] = useUpdateArtifactPortfolioMutation({
    context: propagateErrorsContext(),
  });
  const [updateArtifactCollection] = useUpdateArtifactCollectionMutation({
    context: propagateErrorsContext(),
  });
  const [moveArtifactCollection] = useMoveArtifactCollectionMutation({
    context: propagateErrorsContext(),
  });

  // 6. Panel Business Logic
  // Get the collection data needed for this component via Weave Query
  const artifactCollectionID = useNodeValue(
    React.useMemo(
      () => opArtifactId({artifact: artifactCollectionNode}),
      [artifactCollectionNode]
    )
  );

  const {data: artifactCollectionData, loading: artifactCollectionDataLoading} =
    useArtifactCollectionOverview(artifactCollectionID.result);

  const isPortfolio = artifactCollectionData?.collectionIsPortfolio ?? false;
  const {isProjectReadOnly, loading: isProjectReadOnlyLoading} =
    useIsProjectReadOnly(
      artifactCollectionData?.entityName ?? '',
      artifactCollectionData?.projectName ?? ''
    );

  // 5. Define User Actions
  const setSelectedMembershipIdentifier = React.useCallback(
    (newIdentifier: string) => {
      updateConfig({
        selectedMembershipIdentifier: newIdentifier,
        selectedCollectionView: 'membership',
        // When selecting a membership, we reset to the overview subtab
        tabConfigs: {
          ...config?.tabConfigs,
          overview: {
            ...config?.tabConfigs?.overview,
            selectedTab: 'overview',
          },
        },
      });
      if (artifactCollectionData != null) {
        Analytics.track('Artifact interaction', {
          entityName: artifactCollectionData.entityName,
          projectName: artifactCollectionData.projectName,
          artifactCollectionName: artifactCollectionData.collectionName,
          artifactType: artifactCollectionData.artifactTypeName,
          artifactCollectionView: 'membership',
          artifactCollectionMembership: newIdentifier,
          artifactCollectionType: isPortfolio ? 'portfolio' : 'sequence',
        });
      }
    },
    [updateConfig, config, artifactCollectionData, isPortfolio]
  );

  const setSelectedCollectionView = React.useCallback(
    (newView: ArtifactCollectionViewType) => {
      updateConfig({
        selectedMembershipIdentifier: undefined,
        selectedCollectionView: newView,
        tabConfigs: {
          ...config?.tabConfigs,
          overview: {
            ...config?.tabConfigs?.overview,
            selectedTab: undefined,
          },
        },
      });
      if (artifactCollectionData != null) {
        Analytics.track('Artifact interaction', {
          entityName: artifactCollectionData.entityName,
          projectName: artifactCollectionData.projectName,
          artifactCollectionName: artifactCollectionData.collectionName,
          artifactType: artifactCollectionData.artifactTypeName,
          artifactCollectionView: newView,
          artifactCollectionType: isPortfolio ? 'portfolio' : 'sequence',
        });
      }
    },
    [updateConfig, config, artifactCollectionData, isPortfolio]
  );

  const onSelectVersion = React.useCallback(
    (versionIndex: number) => {
      setSelectedMembershipIdentifier('v' + versionIndex);
    },
    [setSelectedMembershipIdentifier]
  );

  // Get the Weave Membership Node
  const artifactMembershipNode = React.useMemo(() => {
    if (membershipIdentifier == null) {
      return constNone();
    }
    return opArtifactMembershipForAlias({
      artifact: artifactCollectionNode,
      aliasName: constString(membershipIdentifier),
    }) as OutputNode<'artifactMembership'>;
  }, [artifactCollectionNode, membershipIdentifier]);

  // Get the Weave Data Node for the membership version
  const currentVersionDictNode = React.useMemo(() => {
    return opDict({
      index: opArtifactMembershipVersionIndex({
        artifactMembership: artifactMembershipNode,
      }),
      id: opArtifactVersionId({
        artifactVersion: opArtifactMembershipArtifactVersion({
          artifactMembership: artifactMembershipNode,
        }),
      }),
    } as any) as OutputNode<{
      type: 'typedDict';
      propertyTypes: {
        index: 'number';
        id: 'string';
      };
    }>;
  }, [artifactMembershipNode]);

  // Get the current version data needed for this component
  const currentVersionValue = useNodeValue(currentVersionDictNode);

  // Extract some derived properties
  const lastVersionIndex = artifactCollectionData?.lastMembershipVersionIndex;
  const selectedMembershipMissing =
    membershipIdentifier != null &&
    !currentVersionValue.loading &&
    currentVersionValue.result?.id === null;
  const collectionIsEmpty =
    !artifactCollectionID.loading &&
    !artifactCollectionDataLoading &&
    lastVersionIndex == null;

  // 7. Effects
  // Here we want to update the selected membership when viewing the membership tab, under 2 cases:
  // a. No identifier is selected, but there is a default identifier (at least one member).
  // b. An identifier is selected, but it is not present in the collection
  React.useEffect(() => {
    // Default view when no view (collection or membership-level) is given to us from the config or URL
    if (collectionView == null && membershipIdentifier == null) {
      setSelectedCollectionView('versions');
    }
    // If we are looking at the membership tab, and there exists at least one member
    else if (collectionView === 'membership' && !collectionIsEmpty) {
      if (
        membershipIdentifier == null &&
        membershipIdentifier !== `v${lastVersionIndex}`
      ) {
        // If there is no selected membership, or the selected membership is missing,
        // switch to the latest membership
        setSelectedMembershipIdentifier('v' + lastVersionIndex);
      } else if (
        selectedMembershipMissing &&
        membershipIdentifier !== `v${lastVersionIndex}`
      ) {
        setSelectedMembershipIdentifier('v' + lastVersionIndex);
      } else if (selectedMembershipMissing) {
        // If the currentVersionValue is null, that means the viewer doesn't have permission to view it,
        // therefore switch to the versions view
        toast("You don't have permission to view the version details");
        setSelectedCollectionView('versions');
      }
    } else if (collectionView === 'membership' && collectionIsEmpty) {
      setSelectedCollectionView('versions');
    }
    // This includes collections shown in both the legacy model registry and the global registry
    const hasRegistryView =
      artifactCollectionData != null &&
      artifactCollectionData.collectionIsPortfolio &&
      getCollectionRegistryName(
        artifactCollectionData.projectName ?? '',
        artifactCollectionData.artifactTypeName ?? ''
      ) !== '';
    if (collectionView === 'action_history' && !hasRegistryView) {
      // If we're trying to view the action history of a non-registry portfolio collection, redirect to the versions view
      setSelectedCollectionView('versions');
    }

    if (
      // If the selected collection view is not of ArtifactCollectionViewType ('versions', 'membership'
      // or 'action_history'), update the config view to 'versions'
      !['versions', 'membership', 'action_history', undefined].includes(
        collectionView
      )
    ) {
      setSelectedCollectionView('versions');
    }
  }, [
    collectionView,
    lastVersionIndex,
    membershipIdentifier,
    selectedMembershipMissing,
    setSelectedMembershipIdentifier,
    setSelectedCollectionView,
    currentVersionValue.result?.id,
    currentVersionValue.loading,
    collectionIsEmpty,
    updateConfig,
    artifactCollectionData,
  ]);

  // 8. Child Props
  const dropdownOptions = React.useMemo(() => {
    if (artifactCollectionData == null) {
      return [];
    }
    let options: Array<{
      value?: string;
      text?: string;
      header?: boolean;
      headerContent?: string;
      divider?: boolean;
    }> = [{value: 'versions', text: 'All Versions'}];
    if (
      getCollectionRegistryName(
        artifactCollectionData?.projectName ?? '',
        artifactCollectionData?.artifactTypeName ?? ''
      ) !== '' &&
      isPortfolio
    ) {
      options.push({value: 'action_history', text: 'Action History'});
    }
    const versions =
      artifactCollectionData?.versionIndexes?.map(v => {
        const version = `v${v}`;
        return {
          value: version,
          text: version,
        };
      }) ?? [];

    const aliasOptions =
      artifactCollectionData?.aliases?.map(a => {
        return {value: `${a}`, text: a};
      }) ?? [];

    options = [
      ...options,
      {header: true, headerContent: 'Aliases'},
      {divider: true},
      ...aliasOptions,
      {header: true, headerContent: 'Versions'},
      {divider: true},
      ...versions,
    ];

    return options;
  }, [artifactCollectionData, isPortfolio]);

  const dropdownText = React.useMemo(() => {
    if (currentVersionValue.loading) {
      return '';
    }
    if (collectionView === 'action_history') {
      return 'Action History';
    } else if (collectionView === 'versions') {
      return 'Versions';
    } else if (isVersionAlias(membershipIdentifier)) {
      return `Version ${currentVersionValue?.result?.index}`;
    } else {
      return membershipIdentifier;
    }
  }, [
    currentVersionValue?.result?.index,
    currentVersionValue.loading,
    collectionView,
    membershipIdentifier,
  ]);

  const scrolled = useHandleScroll();

  const {selectedAccount} = useContext(AccountSelectorContext);
  const hasAutomationAccess = useIsAutomationEnabled(selectedAccount);
  const accountOrgName = envIsPublicCloud
    ? (selectedAccount?.name ?? '')
    : DEFAULT_ONPREM_ORGANIZATION_NAME;

  if (
    artifactCollectionID.loading ||
    artifactCollectionDataLoading ||
    isProjectReadOnlyLoading
  ) {
    return <WandbLoader name="artifact-collection" />;
  }
  if (currentVersionValue.loading) {
    return (
      <WandbLoader name="artifact-collection-membership" inline size="tiny" />
    );
  }

  const readOnly = readOnlyBasedOnMigrationStatus || isProjectReadOnly;

  if (
    artifactCollectionData == null ||
    artifactCollectionData.collectionName == null ||
    artifactCollectionData.projectName == null
  ) {
    return <div>Error: Could not load</div>;
  }
  const collectionHeaderComp = (
    <S.PanelArtifactHeaderContent>
      <S.PanelArtifactHeaderTitle>
        {artifactCollectionData.collectionName}
        <S.CollectionDropdown
          data-test="artifact-collection-dropdown"
          button
          compact
          scrolling
          text={dropdownText}
          // Set to default modal z-index. This dropdown overlays a lot of things
          // so z-index needs to be high
          style={{zIndex: '1001'}}>
          <Dropdown.Menu>
            {dropdownOptions.map((option, i) => {
              if (option.header === true) {
                return (
                  <Dropdown.Header
                    key={`header-${i}`}
                    content={option.headerContent}
                  />
                );
              } else if (option.divider === true) {
                return <Dropdown.Divider key={`divider-${i}`} />;
              } else {
                return (
                  <Dropdown.Item
                    key={`item-${i}`}
                    text={option.text}
                    value={option.value}
                    onClick={(d, {value}) => {
                      const v = value as string;
                      if (isArtifactCollectionView(v)) {
                        setSelectedCollectionView(v);
                      } else {
                        setSelectedMembershipIdentifier('' + v);
                      }
                    }}
                  />
                );
              }
            })}
          </Dropdown.Menu>
        </S.CollectionDropdown>
      </S.PanelArtifactHeaderTitle>
      {!readOnly && (
        <S.TriggerContainer className={'overview-option-ellipses'}>
          <DropdownMenu.Root open={isMenuOpen} onOpenChange={setIsMenuOpen}>
            <DropdownMenu.Trigger>
              <Button
                size="medium"
                variant="ghost"
                icon="overflow-horizontal"
                active={isMenuOpen}
              />
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content align="end">
                <DropdownMenu.Group>
                  <DropdownMenu.Item
                    onClick={() => setCollectionRenameModalOpen(true)}>
                    <Icon name="pencil-edit" />
                    Rename
                  </DropdownMenu.Item>
                  {!isPortfolio && (
                    <DropdownMenu.Item
                      onClick={() => setMovingCollection(true)}>
                      <Icon name="folder-project-move" />
                      Move
                    </DropdownMenu.Item>
                  )}
                  {props.artifactRegistry && (
                    <DropdownMenu.Item
                      disabled={!hasAutomationAccess}
                      onClick={() => {
                        setAddTriggerModalOpen(true);
                        trackCreateTriggerClicked(
                          'Model registry collection overflow menu'
                        );
                      }}>
                      <Icon name="trigger-alt" />
                      <>
                        Create automation
                        <PremiumFeatureTooltip
                          isPremiumFeatureDisabled={!hasAutomationAccess}
                        />
                      </>
                    </DropdownMenu.Item>
                  )}
                  {props.artifactRegistry && (
                    <DropdownMenu.Item
                      onClick={() => setIsLinkArtifactModalOpen(true)}>
                      <Icon name="add-new" />
                      Link new version
                    </DropdownMenu.Item>
                  )}
                </DropdownMenu.Group>

                {isPortfolio && (
                  <>
                    <DropdownMenu.Separator />
                    <DropdownMenu.Item
                      onClick={() => setCollectionDeleteModalOpen(true)}>
                      <Icon name="delete" />
                      Delete
                    </DropdownMenu.Item>
                  </>
                )}
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        </S.TriggerContainer>
      )}
      <RenameArtifactModal
        name={artifactCollectionData.collectionName}
        modalOpen={collectionRenameModalOpen}
        onRenameArtifact={async newName => {
          try {
            if (isPortfolio) {
              await updateArtifactPortfolio({
                variables: {
                  artifactPortfolioID: artifactCollectionData.collectionId,
                  name: newName,
                },
              });
            } else {
              await updateArtifactCollection({
                variables: {
                  artifactSequenceID: artifactCollectionData.collectionId,
                  name: newName,
                },
              });
            }
          } catch (error) {
            console.error(error);
            toast('Something went wrong while trying to rename this artifact.');
          }
          setCollectionRenameModalOpen(false);
          await refreshAll();
        }}
        onClose={async () => {
          setCollectionRenameModalOpen(false);
        }}
      />
      <MoveArtifactModal
        entityName={artifactCollectionData.entityName ?? ''}
        projectName={artifactCollectionData.projectName ?? ''}
        modalOpen={isMovingCollection}
        onMoveArtifact={async destinationArtifactTypeName => {
          await moveArtifactCollection({
            variables: {
              artifactSequenceID: artifactCollectionData.collectionId,
              destinationArtifactTypeName,
            },
          });
          setMovingCollection(false);
          await refreshAll();
          history.push(
            urls.artifactSequence({
              entityName: artifactCollectionData.entityName ?? '',
              projectName: artifactCollectionData.projectName ?? '',
              artifactTypeName: destinationArtifactTypeName,
              artifactSequenceName: artifactCollectionData.collectionName,
            })
          );
        }}
        onClose={() => setMovingCollection(false)}
      />
      <DeleteArtifactModal
        id={artifactCollectionData.collectionId}
        entityName={artifactCollectionData.entityName ?? ''}
        projectName={artifactCollectionData.projectName ?? ''}
        artifactTypeName={artifactCollectionData.artifactTypeName}
        artifactCollectionName={artifactCollectionData.collectionName}
        isPortfolio={isPortfolio}
        isModalOpen={collectionDeleteModalOpen}
        refetchProjectArtifacts={async () => {
          const {registryName, isRegistryProject} = checkRegistryProject(
            artifactCollectionData.projectName ?? ''
          );
          await refreshAll();
          let url = urls.artifactSequence({
            entityName: artifactCollectionData.entityName ?? '',
            projectName: artifactCollectionData.projectName ?? '',
            artifactTypeName: artifactCollectionData.artifactTypeName,
            artifactSequenceName: artifactCollectionData.collectionName,
          });
          if (isPortfolio) {
            url = resolveRegistryUrl(
              isRegistryProject,
              accountOrgName,
              registryName ?? 'model',
              undefined,
              artifactCollectionData.entityName ?? '',
              'versions',
              undefined,
              'overview'
            );
          }
          history.push(url);
        }}
        setIsDeleteModalOpen={setCollectionDeleteModalOpen}
      />
      {addTriggerModalOpen && (
        <CreateTriggerDrawerForCollection
          open={addTriggerModalOpen}
          onClose={() => {
            setAddTriggerModalOpen(false);
          }}
          projectName={artifactCollectionData.projectName ?? ''}
          entityName={artifactCollectionData.entityName ?? ''}
          projectID={artifactCollectionData.projectId}
          artifactCollectionID={artifactCollectionData.collectionId}
          artifactCollectionName={artifactCollectionData.collectionName}
        />
      )}
      <AddArtifactDrawer
        open={isLinkArtifactModalOpen}
        closeDrawer={() => setIsLinkArtifactModalOpen(false)}
        defaultProjectName={
          artifactCollectionData.projectName?.startsWith(
            REGISTRY_PROJECT_PREFIX
          )
            ? (artifactCollectionData.projectName ?? '')
            : `${artifactCollectionData.artifactTypeName}-registry`
        }
        collection={{
          ...artifactCollectionData,
          name: artifactCollectionData.collectionName,
          id: artifactCollectionData.collectionId,
          numVersions: artifactCollectionData.versionIndexes.length,
          entityName: artifactCollectionData.entityName ?? '',
          projectName: artifactCollectionData.projectName ?? '',
          artifactTypeName: artifactCollectionData.artifactTypeName,
          description: '',
          tags: [],
          aliases: artifactCollectionData.aliases.map(a => a ?? ''),
        }}
        scrollWidth={scrolled.scrollHeight}
      />
    </S.PanelArtifactHeaderContent>
  );

  if (collectionIsEmpty && !props.artifactRegistry) {
    return (
      <S.ArtifactCollectionMain>
        <S.BlankPageWrapper>
          <PanelEmptyArtifact
            {...Panel2.dummyProps}
            input={props.input}
            isPortfolio={isPortfolio}
          />
        </S.BlankPageWrapper>
      </S.ArtifactCollectionMain>
    );
  }

  if (
    MRMigrationStatus === MigrationStatus.COMPLETED &&
    props.artifactRegistry
  ) {
    return (
      <S.ArtifactCollectionMain>
        {collectionHeaderComp}
        <MigrationStatusBanner
          className="m-16"
          entityName={artifactCollectionData.entityName ?? ''}
          projectName={artifactCollectionData.projectName ?? ''}
        />
        <S.PanelWrapper />
      </S.ArtifactCollectionMain>
    );
  }

  // Use this to decide whether to show properties/objects that are specific to registry collections (Tags and Automations)
  const showRegistryObjects = showForRegistry(
    artifactCollectionData.projectName ?? '',
    artifactCollectionData.artifactTypeName,
    isPortfolio,
    props.artifactRegistry ?? false
  );
  return (
    <S.ArtifactCollectionMain>
      {collectionHeaderComp}
      {props.artifactRegistry && (
        <MigrationStatusBanner
          className="m-16"
          entityName={artifactCollectionData.entityName ?? ''}
          projectName={artifactCollectionData.projectName ?? ''}
        />
      )}
      {collectionView === 'action_history' && (
        <S.PanelWrapper>
          <ArtifactActionHistory
            entityName={artifactCollectionData.entityName ?? ''}
            projectName={artifactCollectionData.projectName ?? ''}
            artifactTypeName={artifactCollectionData.artifactTypeName}
            artifactCollectionName={artifactCollectionData.collectionName}
            pageSize={10}
          />
        </S.PanelWrapper>
      )}
      {collectionView === 'versions' && (
        <S.PanelWrapper>
          <S.OverviewPanel>
            <PanelArtifactMembershipOverviewCollectionSection
              {...Panel2.dummyProps}
              readOnly={readOnly}
              artifactRegistry={props.artifactRegistry}
              input={props.input as any}
              inputRef={ref}
            />
          </S.OverviewPanel>
          {!collectionIsEmpty && (
            <S.OverviewPanel>
              <S.VersionsPanelSectionHeader>
                <S.VersionsPanelSectionHeaderTitle>
                  {'Versions'}
                </S.VersionsPanelSectionHeaderTitle>
              </S.VersionsPanelSectionHeader>
              <ArtifactVersionsTable
                onRowClick={onSelectVersion}
                artifactCollectionID={artifactCollectionData.collectionId}
                artifactCollectionName={artifactCollectionData.collectionName}
                entityName={artifactCollectionData.entityName ?? ''}
                projectName={artifactCollectionData.projectName ?? ''}
                artifactRegistry={props.artifactRegistry}
              />
            </S.OverviewPanel>
          )}
          {showRegistryObjects && (
            <S.OverviewPanel>
              <TriggersOverviewTable
                entityName={artifactCollectionData.entityName ?? ''}
                projectName={artifactCollectionData.projectName ?? ''}
                projectID={artifactCollectionData.projectId}
                collectionID={artifactCollectionData.collectionId}
                collectionName={artifactCollectionData.collectionName}
                inputRef={ref}
              />
            </S.OverviewPanel>
          )}
        </S.PanelWrapper>
      )}
      {!selectedMembershipMissing && membershipIdentifier != null && (
        <PanelArtifactMembership
          input={artifactMembershipNode as any}
          config={membershipConfig.config}
          context={props.context}
          updateContext={props.updateContext}
          updateConfig={membershipConfig.updateConfig}
          targetIdentifier={membershipIdentifier}
          artifactRegistry={props.artifactRegistry}
          artifactTypeName={artifactCollectionData.artifactTypeName}
          entityName={artifactCollectionData.entityName ?? ''}
          projectName={artifactCollectionData.projectName ?? ''}
          orgName={accountOrgName}
          artifactCollectionName={artifactCollectionData.collectionName}
          isPortfolio={isPortfolio}
        />
      )}
    </S.ArtifactCollectionMain>
  );
};

export default PanelArtifact;
